import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useAuth } from '../contexts/GoogleAuthContext';
import { myGuidesAdminApi, FeatureFlag, OrchestratorPerson } from '../services/my-guides-admin-api';
import NotificationSnackbar from '../components/NotificationSnackbar';
import PersonSearchBox from '../components/PersonSearchBox';
import {
  ChipKind,
  FlagDraft,
  FlagMode,
  TYPE_AUDIENCES,
  TypeAudience,
  chipsForFlag,
  draftFromFlag,
  isDraftUnchanged,
  isReservedFlagKey,
  isValidFlagKey,
  nameFromEmail,
  payloadFromDraft,
} from '../utils/featureFlags';
import { personInitial, personLabel } from '../utils/people';

// Chip colours follow the Command Center mock (section 4a): green tint for staff and
// other user types, blue for named people, solid green for everyone, grey for off.
const CHIP_STYLES: Record<ChipKind, { bg: string; color: string }> = {
  off: { bg: '#EDEFF5', color: '#676970' },
  everyone: { bg: '#548F65', color: '#FFFFFF' },
  staff: { bg: 'rgba(84,143,101,0.08)', color: '#3C5945' },
  type: { bg: 'rgba(84,143,101,0.08)', color: '#3C5945' },
  people: { bg: '#EEF2FB', color: '#2F4F8F' },
  blocked: { bg: '#FBEDEC', color: '#A82419' },
};

function WhoHasItChips({ flag }: { flag: FeatureFlag }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {chipsForFlag(flag).map((chip) => (
        <Chip
          key={`${chip.kind}-${chip.label}`}
          size="small"
          label={chip.label}
          icon={chip.kind === 'staff' ? <VerifiedIcon style={{ fontSize: 14, color: 'inherit' }} /> : undefined}
          sx={{
            bgcolor: CHIP_STYLES[chip.kind].bg,
            color: CHIP_STYLES[chip.kind].color,
            fontWeight: 600,
          }}
        />
      ))}
    </Box>
  );
}

function formatUpdated(flag: FeatureFlag): string {
  if (!flag.updated_at) return '';
  const date = new Date(flag.updated_at);
  if (Number.isNaN(date.getTime())) return '';
  const who = nameFromEmail(flag.updated_by);
  return `${format(date, 'MMM d, HH:mm')}${who ? ` · ${who}` : ''}`;
}

// -----------------------------------------------------------------------------
// Named / blocked people
// -----------------------------------------------------------------------------

interface PeopleSectionProps {
  title: string;
  helper: string;
  ids: string[];
  otherIds: string[];
  tag: string;
  tagKind: ChipKind;
  disabled: boolean;
  onAdd: (user: OrchestratorPerson) => void;
  onRemove: (personId: string) => void;
}

/** A list of people a flag names (or blocks), with "add by email". */
function PeopleSection({
  title,
  helper,
  ids,
  otherIds,
  tag,
  tagKind,
  disabled,
  onAdd,
  onRemove,
}: PeopleSectionProps) {
  // Names for the ids the flag stores. A failed lookup falls back to the short id.
  const lookups = useQueries(
    ids.map((id) => ({
      queryKey: ['orchestratorUser', id],
      queryFn: () => myGuidesAdminApi.getStaffPerson(id),
      retry: false,
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always' as const,
    }))
  );

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {helper}
      </Typography>

      {!disabled && (
        <PersonSearchBox
          placeholder="Add by email"
          exclude={[...ids, ...otherIds]}
          renderAction={(user, clear) => (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                onAdd(user);
                clear();
              }}
            >
              Add
            </Button>
          )}
        />
      )}

      {ids.map((id, index) => {
        const person: OrchestratorPerson =
          (lookups[index]?.isSuccess && !lookups[index]?.isFetching && !lookups[index]?.isError
            ? lookups[index]?.data : undefined) ?? { personId: id, eligibleForStaff: false };
        const showEmail = person.name && person.email;
        return (
          <Box
            key={id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              py: 1,
              borderBottom: '1px solid #EDEFF5',
            }}
          >
            <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.light' }}>
              {personInitial(person)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {personLabel(person)}
              </Typography>
              {showEmail ? (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {person.email}
                </Typography>
              ) : null}
            </Box>
            <Chip
              size="small"
              label={tag}
              sx={{
                bgcolor: CHIP_STYLES[tagKind].bg,
                color: CHIP_STYLES[tagKind].color,
                fontWeight: 600,
              }}
            />
            {!disabled && (
              <IconButton size="small" aria-label={`Remove ${personLabel(person)}`} onClick={() => onRemove(id)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}
      {ids.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          No one yet.
        </Typography>
      )}
    </Box>
  );
}

// -----------------------------------------------------------------------------
// Drawer: one flag open
// -----------------------------------------------------------------------------

interface FlagDrawerProps {
  flagKey: string;
  flag: FeatureFlag;
  canEdit: boolean;
  staffCount?: number;
  adminCount?: number;
  onClose: () => void;
  onSaved: (message: string) => void;
}

function FlagDrawerContent({
  flagKey,
  flag,
  canEdit,
  staffCount,
  adminCount,
  onClose,
  onSaved,
}: FlagDrawerProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FlagDraft>(() => draftFromFlag(flag));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation(() => myGuidesAdminApi.updateFeatureFlag(flagKey, payloadFromDraft(draft)), {
    onSuccess: (result) => {
      queryClient.invalidateQueries('featureFlags');
      onSaved(result.changed ? `Saved ${flagKey}` : `No changes to ${flagKey}`);
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const unchanged = isDraftUnchanged(flag, draft);
  const nobodyTicked =
    draft.mode === 'types' && draft.typeAudiences.length === 0 && draft.users.length === 0;

  const setMode = (mode: FlagMode) => setDraft((d) => ({ ...d, mode }));
  const toggleType = (audience: TypeAudience) =>
    setDraft((d) => ({
      ...d,
      typeAudiences: d.typeAudiences.includes(audience)
        ? d.typeAudiences.filter((a) => a !== audience)
        : [...d.typeAudiences, audience],
    }));

  // A person is named or blocked, never both.
  const addNamed = (user: OrchestratorPerson) =>
    setDraft((d) => ({
      ...d,
      users: [...d.users, user.personId],
      blockedUsers: d.blockedUsers.filter((id) => id !== user.personId),
    }));
  const addBlocked = (user: OrchestratorPerson) =>
    setDraft((d) => ({
      ...d,
      blockedUsers: [...d.blockedUsers, user.personId],
      users: d.users.filter((id) => id !== user.personId),
    }));

  const peopleCount = (audience: TypeAudience) =>
    audience === 'staff' ? staffCount : audience === 'admin' ? adminCount : undefined;

  const radioCard = (mode: FlagMode, title: string, body: React.ReactNode) => (
    <Paper
      variant="outlined"
      onClick={() => canEdit && setMode(mode)}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: canEdit ? 'pointer' : 'default',
        borderColor: draft.mode === mode ? 'primary.light' : 'divider',
        bgcolor: draft.mode === mode ? 'rgba(84,143,101,0.08)' : 'background.paper',
      }}
    >
      <FormControlLabel
        value={mode}
        disabled={!canEdit}
        control={<Radio size="small" />}
        label={<Typography sx={{ fontWeight: 600 }}>{title}</Typography>}
        sx={{ alignItems: 'center', m: 0 }}
      />
      {body}
    </Paper>
  );

  return (
    <Box sx={{ width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 3, pb: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {flagKey}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {flag.description || flagKey}
          </Typography>
        </Box>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pb: 2, overflowY: 'auto', flex: 1 }}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You can view this flag. Editing needs the developer role or higher.
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          label="Description"
          value={draft.description}
          disabled={!canEdit}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          inputProps={{ maxLength: 300 }}
          sx={{ mb: 2.5 }}
        />

        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
          Who has it
        </Typography>
        <RadioGroup value={draft.mode} onChange={(e) => setMode(e.target.value as FlagMode)}>
          {radioCard(
            'nobody',
            'Nobody',
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
              Off for everyone
            </Typography>
          )}
          {radioCard(
            'types',
            'Types of user, plus named people',
            <Box sx={{ ml: 3.5, mt: 0.5 }}>
              {TYPE_AUDIENCES.map(({ value, label, hint }) => {
                const count = peopleCount(value);
                return (
                  <Box key={value}>
                    <FormControlLabel
                      disabled={!canEdit}
                      control={
                        <Checkbox
                          size="small"
                          checked={draft.typeAudiences.includes(value)}
                          onChange={() => toggleType(value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {label}
                          {count !== undefined && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              {' · '}
                              {count} {count === 1 ? 'person' : 'people'}
                            </Typography>
                          )}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (draft.mode !== 'types' && canEdit) setMode('types');
                      }}
                    />
                    {hint && value === 'admin' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 3.5, mt: -0.5 }}>
                        {hint}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
          {radioCard(
            'everyone',
            'Everyone',
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
              All signed-in users
            </Typography>
          )}
        </RadioGroup>

        {nobodyTicked && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Nothing is ticked and no one is named, so no one will have this. That is the same as Nobody.
          </Alert>
        )}

        <PeopleSection
          title="Named people"
          helper="They get the flag whatever type of user they are, unless they are blocked."
          ids={draft.users}
          otherIds={draft.blockedUsers}
          tag="On"
          tagKind="people"
          disabled={!canEdit}
          onAdd={addNamed}
          onRemove={(id) => setDraft((d) => ({ ...d, users: d.users.filter((u) => u !== id) }))}
        />

        <PeopleSection
          title="Blocked people"
          helper="They never get the flag, even when everyone does."
          ids={draft.blockedUsers}
          otherIds={draft.users}
          tag="Blocked"
          tagKind="blocked"
          disabled={!canEdit}
          onAdd={addBlocked}
          onRemove={(id) =>
            setDraft((d) => ({ ...d, blockedUsers: d.blockedUsers.filter((u) => u !== id) }))
          }
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 2, px: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Saved to the audit log
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canEdit || unchanged || mutation.isLoading}
          onClick={() => {
            setError(null);
            mutation.mutate();
          }}
        >
          {mutation.isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

// -----------------------------------------------------------------------------
// New flag
// -----------------------------------------------------------------------------

function NewFlagDialog({
  open,
  existingKeys,
  onClose,
  onCreated,
}: {
  open: boolean;
  existingKeys: string[];
  onClose: () => void;
  onCreated: (key: string) => void;
}) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKey('');
      setDescription('');
      setError(null);
    }
  }, [open]);

  const trimmed = key.trim();
  const keyError =
    trimmed && !isValidFlagKey(trimmed)
      ? 'Lower-case letters, digits and underscores, with optional dot-separated parts (my_guides.plan_trip)'
      : trimmed && isReservedFlagKey(trimmed)
      ? 'That key is worked out for each person by the server and is never stored'
      : trimmed && existingKeys.includes(trimmed)
      ? 'That flag already exists'
      : null;

  // A new flag starts off for everyone; it is opened up from the drawer afterwards.
  // Create-only: if the key already exists (seeded, or made by someone else since this
  // list loaded) the server refuses with 409 and the live flag is left exactly as it is.
  const mutation = useMutation(
    () =>
      myGuidesAdminApi.createFeatureFlag(trimmed, {
        enabled: false,
        description: description.trim(),
        audiences: [],
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('featureFlags');
        onCreated(trimmed);
      },
      onError: (e: Error) => {
        // Refresh the list so an already-existing flag shows up in it.
        queryClient.invalidateQueries('featureFlags');
        setError(e.message);
      },
    }
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New flag</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          margin="dense"
          label="Key"
          placeholder="my_guides.plan_trip"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          error={Boolean(keyError)}
          helperText={keyError ?? 'The name the apps ask for. It cannot be renamed later.'}
          inputProps={{ style: { fontFamily: 'monospace' } }}
        />
        <TextField
          fullWidth
          size="small"
          margin="dense"
          label="What it controls"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 300 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          New flags start off for everyone. Open the flag afterwards to choose who gets it.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!trimmed || Boolean(keyError) || mutation.isLoading}
          onClick={() => {
            setError(null);
            mutation.mutate();
          }}
        >
          {mutation.isLoading ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

export default function FeatureFlags() {
  const { user, hasPermission } = useAuth();
  const canEdit =
    (user?.role === 'developer' || user?.role === 'admin') && hasPermission('feature_flags', 'edit');
  const canViewStaff = hasPermission('staff', 'view');

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [newFlagOpen, setNewFlagOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const { data, isLoading, error } = useQuery('featureFlags', myGuidesAdminApi.getFeatureFlags);
  // Only for the "· N people" hints in the drawer; the page works without them.
  const staffQuery = useQuery('staff', myGuidesAdminApi.getStaff, { enabled: canViewStaff, retry: false });

  const flags = data?.flags ?? {};
  const keys = useMemo(() => Object.keys(flags).sort(), [flags]);
  const selectedFlag = selectedKey ? flags[selectedKey] : undefined;

  const staffMembers = staffQuery.data;
  const staffCount = staffMembers?.filter((m) => m.tier === 'staff' || m.tier === 'admin').length;
  const adminCount = staffMembers?.filter((m) => m.tier === 'admin').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 2.5, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Feature flags
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Turn features on for staff, chosen people, or everyone. Changes apply within a minute.
          </Typography>
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewFlagOpen(true)}>
            New flag
          </Button>
        )}
      </Box>

      {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="medium">
            <TableHead sx={{ bgcolor: '#F7F7FA' }}>
              <TableRow>
                <TableCell>Flag</TableCell>
                <TableCell>Who has it</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => {
                const flag = flags[key];
                return (
                  <TableRow
                    key={key}
                    hover
                    selected={key === selectedKey}
                    onClick={() => setSelectedKey(key)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {flag.description || key}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                        {key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <WhoHasItChips flag={flag} />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{formatUpdated(flag)}</TableCell>
                  </TableRow>
                );
              })}
              {keys.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    No flags yet. Run scripts/seed_feature_flags.py to add the My guides flags
                    {canEdit ? ', or create one with New flag.' : '.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Drawer anchor="right" open={Boolean(selectedFlag)} onClose={() => setSelectedKey(null)}>
        {selectedKey && selectedFlag ? (
          <FlagDrawerContent
            key={selectedKey}
            flagKey={selectedKey}
            flag={selectedFlag}
            canEdit={canEdit}
            staffCount={staffCount}
            adminCount={adminCount}
            onClose={() => setSelectedKey(null)}
            onSaved={(message) => setSnackbar({ open: true, message, severity: 'success' })}
          />
        ) : null}
      </Drawer>

      <NewFlagDialog
        open={newFlagOpen}
        existingKeys={keys}
        onClose={() => setNewFlagOpen(false)}
        onCreated={(key) => {
          setNewFlagOpen(false);
          setSnackbar({ open: true, message: `Created ${key}`, severity: 'success' });
          setSelectedKey(key);
        }}
      />

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
}
